// ============================================================
// CUSTOMER TYPES
// ============================================================

// ============================================================
// PHONE TYPES
// ============================================================

// Phone types for customer phones
export const PHONE_TYPES = [
  { value: 'mobile', label: 'Mobile' },
  { value: 'home', label: 'Home' },
  { value: 'work', label: 'Work' },
  { value: 'other', label: 'Other' },
] as const;

export type PhoneType = (typeof PHONE_TYPES)[number]['value'];

// Customer phone from database (customer_phones table)
export interface CustomerPhone {
  id: string;
  business_id: string;
  customer_id: string;
  phone_number: string;
  phone_type: PhoneType;
  label: string | null;
  is_primary: boolean;
  can_sms: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

// Phone summary from v_customers_list.all_phones JSONB
export interface CustomerPhoneSummary {
  id: string;
  phone_number: string;
  phone_type: PhoneType;
  label: string | null;
  is_primary: boolean;
  can_sms: boolean;
  notes: string | null;
}

// Input for creating/updating a phone
export interface PhoneInput {
  phone_number: string;
  phone_type?: PhoneType;
  label?: string | null;
  is_primary?: boolean;
  can_sms?: boolean;
  notes?: string | null;
}

// Form state for a single phone input
export interface PhoneFormItem {
  tempId: string;
  phoneNumber: string;
  phoneType: PhoneType;
  label: string;
  isPrimary: boolean;
  canSms: boolean;
  notes: string;
  // For existing phones being edited
  existingId?: string;
}

// Default phone form item
export const createDefaultPhoneFormItem = (
  isPrimary: boolean = false
): PhoneFormItem => ({
  tempId: crypto.randomUUID(),
  phoneNumber: '',
  phoneType: 'mobile',
  label: '',
  isPrimary,
  canSms: true,
  notes: '',
});

// ============================================================
// ADDRESS TYPES
// ============================================================

// Canadian provinces/territories for address dropdown
export const CANADIAN_PROVINCES = [
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland and Labrador' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'NT', label: 'Northwest Territories' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'ON', label: 'Ontario' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'QC', label: 'Quebec' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'YT', label: 'Yukon' },
] as const;

export type CanadianProvince = (typeof CANADIAN_PROVINCES)[number]['value'];

// Countries (Canada default, but expandable)
export const COUNTRIES = [
  { value: 'Canada', label: 'Canada' },
  { value: 'United States', label: 'United States' },
] as const;

export type Country = (typeof COUNTRIES)[number]['value'];

// Base customer from database
export interface Customer {
  id: string;
  business_id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  billing_street_address: string | null;
  billing_unit: string | null;
  billing_city: string | null;
  billing_province: string | null;
  billing_postal_code: string | null;
  billing_country: string | null;
  created_at: string;
  updated_at: string;
}

// Customer from v_customers_list view (includes aggregated data)
export interface CustomerListItem extends Customer {
  full_name: string;
  business_name: string | null;
  project_count: number;
  // Phone fields from updated view
  all_phones: CustomerPhoneSummary[];
  phone_count: number;
}

// Input for creating a new customer
export interface CreateCustomerInput {
  business_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  billing_street_address: string;
  billing_unit?: string;
  billing_city: string;
  billing_province: string;
  billing_postal_code: string;
  billing_country: string;
}

// Input for updating a customer
export interface UpdateCustomerInput {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string | null;
  billing_street_address?: string;
  billing_unit?: string | null;
  billing_city?: string;
  billing_province?: string;
  billing_postal_code?: string;
  billing_country?: string;
}

// ============================================================
// PROJECT TYPES
// ============================================================

// Project statuses
export const PROJECT_STATUSES = [
  { value: 'planned', label: 'Planned' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
] as const;

export type ProjectStatus = (typeof PROJECT_STATUSES)[number]['value'];

// Base project from database
export interface Project {
  id: string;
  business_id: string;
  customer_id: string;
  project_name: string;
  status: ProjectStatus;
  start_date: string | null;
  end_date: string | null;
  created_at: string;
  updated_at: string;
}

// Project customer roles
export const PROJECT_CUSTOMER_ROLES = [
  { value: 'primary', label: 'Primary Contact' },
  { value: 'billing', label: 'Billing Contact' },
  { value: 'contact', label: 'Additional Contact' },
  { value: 'stakeholder', label: 'Stakeholder' },
] as const;

export type ProjectCustomerRole = (typeof PROJECT_CUSTOMER_ROLES)[number]['value'];

// Project customer from junction table
export interface ProjectCustomer {
  id: string;
  business_id: string;
  project_id: string;
  customer_id: string;
  role: ProjectCustomerRole;
  is_billing: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

// Project customer from v_project_customers view
export interface ProjectCustomerView extends ProjectCustomer {
  first_name: string;
  last_name: string;
  customer_name: string;
  email: string | null;
  phone: string | null;
  project_name: string;
  project_status: ProjectStatus;
}

// Summary of customer for embedding in project (from all_customers JSONB)
export interface ProjectCustomerSummary {
  id: string;
  customer_id: string;
  role: ProjectCustomerRole;
  is_billing: boolean;
  first_name: string;
  last_name: string;
  name: string;
  email: string | null;
  phone: string | null;
}

// Project from v_projects_with_customer view
export interface ProjectWithCustomer extends Project {
  // Primary customer (backward compatible)
  customer_first_name: string;
  customer_last_name: string;
  customer_name: string;
  customer_email: string | null;
  customer_phone: string | null;
  customer_billing_street_address: string | null;
  customer_billing_unit: string | null;
  customer_billing_city: string | null;
  customer_billing_province: string | null;
  customer_billing_postal_code: string | null;
  customer_billing_country: string | null;
  customer_full_address: string | null;
  // Billing customer (may differ from primary)
  billing_customer_id: string | null;
  billing_customer_first_name: string | null;
  billing_customer_last_name: string | null;
  billing_customer_name: string | null;
  billing_customer_email: string | null;
  // All customers
  all_customers: ProjectCustomerSummary[];
  customer_count: number;
  // Business
  business_name: string | null;
  // Site location (primary project site)
  site_street_address: string | null;
  site_unit: string | null;
  site_city: string | null;
  site_province: string | null;
  site_postal_code: string | null;
  site_country: string | null;
  site_full_address: string | null;
  site_lat: number | null;
  site_lng: number | null;
  has_site_location: boolean;
}

// ============================================================
// PROJECT SITE TYPES
// ============================================================

// Project site from database
export interface ProjectSite {
  id: string;
  business_id: string;
  project_id: string;
  site_name: string | null;
  is_primary: boolean;
  street_address: string | null;
  unit: string | null;
  city: string | null;
  province: string | null;
  postal_code: string | null;
  country: string | null;
  location_radius_m: number;
  address_cache_id: string | null;
  created_at: string;
  updated_at: string;
}

// Input for creating a project site
export interface CreateProjectSiteInput {
  business_id: string;
  project_id: string;
  site_name?: string | null;
  is_primary?: boolean;
  street_address?: string | null;
  unit?: string | null;
  city?: string | null;
  province?: string | null;
  postal_code?: string | null;
  country?: string | null;
}

// Input for adding a customer to a project
export interface AddProjectCustomerInput {
  project_id: string;
  customer_id: string;
  role?: ProjectCustomerRole;
  is_billing?: boolean;
  notes?: string;
}

// Input for updating a project customer
export interface UpdateProjectCustomerInput {
  role?: ProjectCustomerRole;
  is_billing?: boolean;
  notes?: string | null;
}

// Input for creating a new project
export interface CreateProjectInput {
  business_id: string;
  customer_id: string;
  project_name: string;
  status?: ProjectStatus;
  start_date?: string | null;
  end_date?: string | null;
}

// Input for updating a project
export interface UpdateProjectInput {
  project_name?: string;
  status?: ProjectStatus;
  start_date?: string | null;
  end_date?: string | null;
}

// ============================================================
// COMBINED TYPES FOR PROJECT CREATION FLOW
// ============================================================

// Individual customer item in the multi-customer form
export interface ProjectCustomerFormItem {
  tempId: string; // For React key
  mode: 'new' | 'existing';
  existingCustomerId?: string;
  existingCustomerName?: string;
  // New customer fields
  firstName: string;
  lastName: string;
  email: string;
  phones: PhoneFormItem[]; // Multi-phone support
  billingStreetAddress: string;
  billingUnit: string;
  billingCity: string;
  billingProvince: string;
  billingPostalCode: string;
  billingCountry: string;
  // Role fields
  isPrimary: boolean;
  isBilling: boolean;
}

// Default values for a new customer form item
export const createDefaultCustomerFormItem = (
  isPrimary: boolean = false,
  isBilling: boolean = false
): ProjectCustomerFormItem => ({
  tempId: crypto.randomUUID(),
  mode: 'new',
  existingCustomerId: undefined,
  existingCustomerName: undefined,
  firstName: '',
  lastName: '',
  email: '',
  phones: [createDefaultPhoneFormItem(true)], // Start with one primary phone
  billingStreetAddress: '',
  billingUnit: '',
  billingCity: '',
  billingProvince: 'ON',
  billingPostalCode: '',
  billingCountry: 'Canada',
  isPrimary,
  isBilling,
});

// Form data for creating a project with multiple customers
export interface ProjectCreateFormData {
  // Multi-customer support
  customers: ProjectCustomerFormItem[];

  // Project fields
  projectName: string;
  projectStatus: ProjectStatus;
  projectStartDate: string;
  projectEndDate: string;

  // Site location fields
  useSameAsCustomerAddress: boolean;
  siteStreetAddress: string;
  siteUnit: string;
  siteCity: string;
  siteProvince: string;
  sitePostalCode: string;
  siteCountry: string;
}

// Default form values
export const DEFAULT_PROJECT_FORM_DATA: ProjectCreateFormData = {
  customers: [createDefaultCustomerFormItem(true, true)], // First customer is primary and billing
  projectName: '',
  projectStatus: 'planned',
  projectStartDate: '',
  projectEndDate: '',
  // Site location defaults
  useSameAsCustomerAddress: true,
  siteStreetAddress: '',
  siteUnit: '',
  siteCity: '',
  siteProvince: 'ON',
  sitePostalCode: '',
  siteCountry: 'Canada',
};